import React, { useCallback, useState } from 'react';
import { View, Text } from 'react-native';
import { connect } from 'react-redux';
import i18n from 'i18n-js';

import TopPanel from '@components/operatorPanel/components/OperatorPanelInformation/TopPanel';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { fullWidth, scaleOP } from '@components/frame';
import { ColorFunction } from '@styles/colors/types';
import { getLinkingModalConfig, Popup, triggerRemovePI, Alert } from '@helpers';
import ContextMenuModal, { ContextMenuModalItem } from '@components/operatorPanel/components/elements/ContextMenuModal';
import { fontOP } from '@styles/fonts';
import ContextMenuButton from '@components/operatorPanel/components/elements/buttons/ContextMenuButton';
import AddButtonWithHover from '@components/operatorPanel/components/elements/buttons/AddButtonWithHover';
import SendToMachineButton from './SendToMachineButton';
import NCFileList from './NCFileList';
import { useNCFileManagement } from './useNCFileManagement';

import { ReducerEntityWithLoadingState } from '@v2/pages/common/interfaces';
import { NCFileSearchItem, OperationSearchItem } from '@interfaces/search';
import NCFileContentWithVersion from './NCFileContentWithVersion';

import NCFileType from '@components/common/NCFileType';
import { incrementTabCount } from '@reducers/operatorPanel';
import { removeProductionInformation } from '@reducers/manufacture';
import { setActionsModalOpen, setIsRemovingPI } from '@reducers/machinesV2';
import { sendNcFileToMachine } from '@reducers/machines';
import { GenerateNCFileURL, RemoveProductionInformation, SearchEntity } from '@interfaces/methods';
import { generateNCFileURL } from '@reducers/blobStorage';
import { shareNCFile } from './helpers';
import {
  clearNcFileTypeActionConfig,
  downloadActionConfig,
  modifyNcFileTypeActionConfig,
  sendToMachineActionConfig,
  unlinkNcFileFromJobActionConfig,
} from './actionContextMenuConfig';
import LargeAddButton from '@components/operatorPanel/components/elements/buttons/LargeAddButton';
import Loading from '@components/loading';
import { useFocusEffect, useRoute } from '@react-navigation/native';
import { operationsSearch } from '@reducers/search';
import { scheduledProductionOperationIdParam } from '@components/operatorPanel/constants/operatorPanel.constants';

interface OperatorPanelInformationNcFilesScreenProps {
  C: ColorFunction;
  setActionsModalOpen: (state: boolean) => void;
  isRemovingPI: boolean;
  setIsRemovingPI: (state: boolean) => void;
  removeProductionInformation: RemoveProductionInformation;
  generateNCFileURL: GenerateNCFileURL;
  sendNcFileToMachine: (value: any) => Promise<any>;
  incrementTabCount: (value: any) => any;
  isWithERP: boolean;
  selectedItem: any;
  batch: any;
  operationsSearch: SearchEntity<OperationSearchItem>;
}

const RenderNcFile = ({ currentNCFile, bg, C }: any) => {
  if (currentNCFile.status === 'uploading') {
    return <Loading background={C(bg)} />;
  }
  if (currentNCFile.status === 'error') {
    return <Text style={{ marginTop: scaleOP(20) }}>{i18n.t('Error during uploading')}</Text>;
  }
  return <NCFileContentWithVersion C={C} style={{ marginTop: scaleOP(24) }} scale={scaleOP} font={fontOP} ncFileId={currentNCFile.ncFileId} bg={bg} />;
};

const OperatorPanelInformationNcFilesScreen = ({
  C,
  isRemovingPI,
  removeProductionInformation,
  setActionsModalOpen,
  setIsRemovingPI,
  generateNCFileURL,
  sendNcFileToMachine,
  isWithERP,
  selectedItem,
  batch,
  incrementTabCount,
  operationsSearch,
}: OperatorPanelInformationNcFilesScreenProps) => {
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [isNCFileSending, setNCFileSending] = useState(false);
  const [jobs, setJobs] = useState<ReducerEntityWithLoadingState<OperationSearchItem[]>>({ data: [], wasLoaded: false });

  const route = useRoute();
  const selectedJobId = route.params?.[scheduledProductionOperationIdParam];

  const jobIds = selectedJobId != null ? [+selectedJobId] : [...new Set(batch?.data?.links?.map((l) => l.productionOperationId))];

  const jobIdsHash = jobIds.join(', ');

  useFocusEffect(
    useCallback(() => {
      if (jobIds.length === 0) {
        return;
      }

      (async () => {
        const jobsData = await operationsSearch({
          data: {
            select: [
              'LinkedNcFiles',
              'PartId',
              'PartNumber',
              'ProductionOperationId',
              'ProductionOperationNumber',
              'ProductionOrderId',
              'ProductionOrderNumber',
            ],
          },
          filter: { oDataFilter: `ProductionOperationId eq ${jobIds.join(' or ProductionOperationId eq ')}`, orderBy: ['ModifiedOn desc'] },
          select: null,
          resetHistory: true,
          ignoreHistory: true,
        });

        if (jobsData?.searchResult?.length) {
          const jobsResult = { data: jobsData.searchResult, wasLoaded: true };
          setJobs(jobsResult);
        }
      })();
    }, [jobIdsHash])
  );

  // Use the custom hook for NC file management
  const { currentNCFile, currentNCFileLoaded, toggleNCFileUpload, isMachinePerspective, ncFileListProps } = useNCFileManagement({
    jobs,
    setJobs,
    batch,
    incrementTabCount,
  });

  const linkingModalConfig = getLinkingModalConfig(scaleOP, fullWidth);

  const theme = C.scheme === 'light' ? 'Light' : 'Dark';
  const bg = C(`Background/${theme}/Panel [mobile]`, false);
  const ncFileBg = 'Background/Light/Field on Panel';

  const downloadNCFile = async () => {
    try {
      await shareNCFile({ generateNCFileURL, ncFileData: currentNCFile });
    } catch (error) {
      console.error(error);
    }
  };

  const handleClearNCFile = () => {
    if (!isRemovingPI && currentNCFile) {
      triggerRemovePI({
        params: { ncFileId: currentNCFile.ncFileId },
        removeProductionInformation,
        setActionsModalOpen,
        setIsRemovingPI,
      });
    }
  };

  // TODO: Re-enable when unlinking NC files is fixed from the backend
  // const handleUnlinkNcFileFromJob = () => {
  //   if (!isRemovingPI && currentNCFile && selectedJobId != null) {
  //     triggerRemovePI({
  //       params: { ncFileId: currentNCFile.ncFileId, productionOperationId: +selectedJobId },
  //       removeProductionInformation,
  //       setActionsModalOpen,
  //       setIsRemovingPI,
  //     });
  //   }
  // };

  const handleChangeNCFileType = () => {
    if (!currentNCFile) {
      return;
    }

    Popup({
      Component: (p: any) => <NCFileType {...{ ...p, ncFileId: currentNCFile?.ncFileId, ncFileName: currentNCFile?.ncFileId, context: 'batch' }} />,
      ...linkingModalConfig,
    });
  };

  const handleSendToMachine = async () => {
    if (!currentNCFile || isNCFileSending) {
      return;
    }

    setNCFileSending(true);

    const result = await sendNcFileToMachine({
      $type: 'favoriteVersion',
      machineId: selectedItem.id,
      ncFileId: currentNCFile.ncFileId,
    });

    setNCFileSending(false);

    if (result.errorMessage || result.errorType) {
      Alert({ type: 'danger', title: result.errorMessage } as any);
      return;
    }

    Alert({ type: 'success', title: i18n.t('Sent to folder {{folder}}: on Machine', { folder: result.machineFolderName || 'Unknown' }) } as any);
  };

  const actionConfig: ContextMenuModalItem[] = [
    { ...downloadActionConfig, onPress: downloadNCFile },
    { ...sendToMachineActionConfig, ...(isNCFileSending ? { title: 'Sending...' } : {}), onPress: handleSendToMachine },
    // TODO: Re-enable when unlinking NC files is fixed from the backend
    // ...(selectedJobId != null ? [{ ...unlinkNcFileFromJobActionConfig, onPress: handleUnlinkNcFileFromJob }] : []),
    { ...modifyNcFileTypeActionConfig, onPress: handleChangeNCFileType },
    { ...clearNcFileTypeActionConfig, onPress: handleClearNCFile },
  ];

  if (((isMachinePerspective && !batch.wasLoaded) || (!isMachinePerspective && !jobs.wasLoaded)) && !currentNCFileLoaded) {
    return <Loading background={bg} />;
  }

  return (
    <View testID={TEST_IDS.operatorPanel.information.ncFiles.root} style={{ backgroundColor: bg, flex: 1 }}>
      <ContextMenuModal
        C={C}
        list={actionConfig}
        onClose={() => setShowContextMenu(false)}
        font={fontOP}
        isVisible={showContextMenu}
        scale={scaleOP}
        globalItemStyle={{ gap: scaleOP(69) }}
      />
      <TopPanel title="NC File" withBottomBorder={!currentNCFile} submit={() => {}}>
        {currentNCFile ? (
          <SendToMachineButton C={C} scale={scaleOP} font={fontOP} onPress={handleSendToMachine} isNCFileSending={isNCFileSending} />
        ) : null}
        {isWithERP && <AddButtonWithHover C={C} scale={scaleOP} onPress={toggleNCFileUpload} />}
        {currentNCFile ? <ContextMenuButton C={C} scale={scaleOP} showContextMenu={showContextMenu} onShowContextMenu={setShowContextMenu} /> : null}
      </TopPanel>
      {currentNCFile ? (
        <View style={{ paddingHorizontal: scaleOP(24), flex: 1 }}>
          <NCFileList withERP={isWithERP} C={C} scale={scaleOP} font={fontOP} {...ncFileListProps} />
          <RenderNcFile {...{ currentNCFile, bg: ncFileBg, C, }} />
        </View>
      ) : (
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <LargeAddButton onPress={toggleNCFileUpload} title="NC File" titleIconName="markdownIcon" scale={scaleOP} C={C} font={fontOP} />
        </View>
      )}
    </View>
  );
};

export default connect(
  ({
    membership: { isWithERP },
    details: {
      ncFileData: { data },
    },
    machinesV2: { isRemovingPI },
    machines: { selectedItem },
    batch: { batch },
  }: {
    details: { ncFileData: { data: NCFileSearchItem | null } };
    machinesV2: { isRemovingPI: boolean };
    machines: { selectedItem: any };
    batch: { batch: any };
    membership: { isWithERP: boolean };
  }) => {
    return { isRemovingPI, ncFileData: data, isWithERP, selectedItem, batch };
  },
  {
    removeProductionInformation,
    setActionsModalOpen,
    setIsRemovingPI,
    generateNCFileURL,
    sendNcFileToMachine,
    incrementTabCount,
    operationsSearch,
  }
)(OperatorPanelInformationNcFilesScreen);
